import React from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const switchClasses = cva(
  'relative inline-flex items-center cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        small: 'h-5 w-9',
        medium: 'h-6 w-11',
        large: 'h-7 w-14',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  }
);

const switchTrackClasses = cva(
  'absolute inset-0 rounded-md transition-colors duration-200',
  {
    variants: {
      checked: {
        true: 'bg-green-500',
        false: 'bg-gray-300',
      },
    },
  }
);

const switchThumbClasses = cva(
  'absolute top-0.5 left-0.5 bg-white rounded-full shadow-md transition-transform duration-200',
  {
    variants: {
      size: {
        small: 'h-4 w-4',
        medium: 'h-5 w-5',
        large: 'h-6 w-6',
      },
    },
    compoundVariants: [
      {
        size: 'small',
        checked: true,
        class: 'translate-x-4',
      },
      {
        size: 'medium',
        checked: true,
        class: 'translate-x-5',
      },
      {
        size: 'large',
        checked: true,
        class: 'translate-x-7',
      },
    ],
  }
);

const Switch = ({
  // Core props
  checked = false,
  onChange,
  disabled = false,
  size = 'medium',
  
  // Accessibility
  id,
  name,
  'aria-label': ariaLabel,
  
  // Optional/ignored (for backward compat, but no effect)
  layout_width,
  position,
  variant,
  className,
  ...props
}) => {
  // Ignore unused props (no classes added for them)
  // Fully controlled: use 'checked' prop directly

  const handleToggle = () => {
    if (disabled || typeof onChange !== 'function') return;
    onChange(!checked);
  };

  const handleKeyDown = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <div
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      className={twMerge(
        switchClasses({ size }),
        'focus:ring-green-500', // Matches checked color
        className
      )}
      {...props}
    >
      <div className={switchTrackClasses({ checked })} />
      <div className={switchThumbClasses({ size, checked })} />
      {/* Optional: Keep SR-only input for form submission if needed */}
      <input
        type="checkbox"
        id={id}
        name={name}
        checked={checked}
        onChange={() => {}} // No-op; div handles toggles
        className="sr-only"
        disabled={disabled}
      />
    </div>
  );
};

export default Switch;
